<script setup lang="ts">
import { computed } from 'vue';
import Modal from './Modal.vue';
import Button from '../forms/Button.vue';
import LumiIcon from '../icons/LumiIcon.vue';

// Blocking notice built on top of Modal. Covers errors/warnings/info/success
// AND confirmations. `mode` mechanically enforces the button rule, so an
// inconsistent footer is impossible to build. Headless: all text via
// props/slots, all behaviour via emits — the host app owns logic and i18n.
// Notices never expose an internal correlation/error id to the user.
const props = withDefaults(
  defineProps<{
    open?: boolean;
    variant?: 'error' | 'warning' | 'info' | 'success';
    mode?: 'acknowledge' | 'decision';
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmTone?: 'primary' | 'danger';
    busy?: boolean;
    dismissible?: boolean;
  }>(),
  {
    open: false,
    variant: 'info',
    mode: 'acknowledge',
    confirmTone: 'primary',
    busy: false,
    dismissible: true,
  },
);

const emit = defineEmits<{ confirm: []; cancel: []; close: [] }>();

// A busy dialog is mid-action: block backdrop/Escape/✕ so the user can't
// dismiss the work it represents.
const modalDismissible = computed(() => props.dismissible && !props.busy);
</script>

<template>
  <Modal
    :open="open"
    :title="title"
    :dismissible="modalDismissible"
    layer="confirm"
    @close="emit('close')"
  >
    <!-- `data-test` marks THIS modal as the notice channel (issue 195). `.lumi-modal`
         alone does not: every ordinary dialog in the apps is one too, so a test that
         counts modals cannot say whether a notice appeared. -->
    <div
      class="lumi-notice"
      :class="`lumi-notice--${variant}`"
      data-test="notice-dialog"
    >
      <span class="lumi-notice__icon" aria-hidden="true">
        <slot name="icon">
          <!-- error -->
          <LumiIcon v-if="variant === 'error'" name="error" width="22" height="22" stroke-width="2" />
          <!-- warning -->
          <LumiIcon v-else-if="variant === 'warning'" name="warning" width="22" height="22" stroke-width="2" />
          <!-- success -->
          <LumiIcon v-else-if="variant === 'success'" name="done" width="22" height="22" stroke-width="2" />
          <!-- info -->
          <LumiIcon v-else name="info" width="22" height="22" stroke-width="2" />
        </slot>
      </span>
      <div class="lumi-notice__body"><slot /></div>
    </div>

    <template #footer>
      <Button
        v-if="mode === 'decision'"
        variant="secondary"
        :disabled="busy"
        @click="emit('cancel')"
      >
        {{ cancelLabel }}
      </Button>
      <Button
        :variant="confirmTone === 'danger' ? 'danger' : 'primary'"
        :loading="busy"
        @click="emit('confirm')"
      >
        {{ confirmLabel }}
      </Button>
    </template>
  </Modal>
</template>

<style>
.lumi-notice{display:flex;gap:var(--space-4);align-items:flex-start;}
.lumi-notice__icon{flex:none;display:flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:var(--radius-pill);}
.lumi-notice--error .lumi-notice__icon{background:var(--danger-soft);color:var(--danger-text);}
.lumi-notice--warning .lumi-notice__icon{background:var(--warning-soft);color:var(--warning-text);}
.lumi-notice--info .lumi-notice__icon{background:var(--info-soft);color:var(--info-text);}
.lumi-notice--success .lumi-notice__icon{background:var(--success-soft);color:var(--success-text);}
/* white-space: pre-line (issue #364, F52) — the sign-out drafts confirm is the
   first caller whose body is more than one sentence: an intro line, a blank
   line, then one line per draft. A plain string body is the composable's own
   contract (`useDialog.ts`'s `ConfirmOptions.body: string`), so a caller that
   needs structure joins it with `\n` rather than passing markup/HTML — this is
   the one CSS rule that makes those newlines visible instead of collapsing
   them the way HTML text normally would. */
.lumi-notice__body{min-width:0;padding-top:var(--space-2);font-size:var(--text-sm);color:var(--text-body);line-height:1.5;white-space:pre-line;}
</style>
